import React, { useEffect, useMemo, useState } from "react";
import { useResetPassword } from "../../hooks/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  alpha,
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline,
  Lock as LockIcon,
  LockReset,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import PasswordStrengthMeter from "../../components/common/PasswordStrengthMeter";
import { isPasswordCompliant } from "../../utils/passwordPolicy";
import { ApiError } from "../../error/errorTypes";
import { FORGOT_PASSWORD_ROUTE_PATH, LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import BrandIcon from "../../components/common/BrandIcon";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);

  const { mutate: resetPassword, isPending: isSubmitting } = useResetPassword();

  const canSubmit = Boolean(token) && isPasswordCompliant(password) && password === confirmPassword && !isSubmitting;

  useEffect(() => {
    if (!success) return;
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(LOGIN_ROUTE_PATH);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [success, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    resetPassword(
      { token, newPassword: password },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message);
          } else {
            setError("Password reset failed. The link may be invalid or expired.");
          }
        },
      }
    );
  };

  const inputSx = {
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiOutlinedInput-root": {
      background: alpha(theme.palette.background.paper, 0.62),
      borderRadius: theme.radii?.medium ?? 10,
      color: theme.palette.text.primary,
      transition: "all 0.22s ease",
      "& input": { color: theme.palette.text.primary },
      "& input:-webkit-autofill": {
        WebkitTextFillColor: theme.palette.text.primary,
        caretColor: theme.palette.text.primary,
        WebkitBoxShadow: `0 0 0 100px ${alpha(theme.palette.background.paper, 0.62)} inset`,
        borderRadius: theme.radii?.medium ?? 10,
        transition: "background-color 9999s ease-out, color 9999s ease-out",
      },
      "& fieldset": { borderColor: alpha(theme.palette.text.primary, 0.1) },
      "&:hover fieldset": { borderColor: alpha(theme.palette.text.primary, 0.22) },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main, borderWidth: 2 },
      "&.Mui-focused": { boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.2)}` },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 18% 16%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 38%), ${theme.palette.background.default}`,
        display: "flex",
        alignItems: "center",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={700}>
          <SurfaceCard
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 2.6, sm: 3.2, md: 3.4 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                  borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                  background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                }}
              >
                <Box sx={{ mb: 2.2 }}>
                  <BrandIcon size={48} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                  }}
                >
                  Create a new password
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                  Use a strong password you have not used before to secure your account.
                </Typography>

                <Stack spacing={1.1} sx={{ mt: 2.4, maxWidth: 360 }}>
                  <Typography variant="body2" color="text.secondary">
                    Choose a password that is unique to this account and hard for others to guess.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    If this link no longer works, request a new reset email and use the latest one.
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                {!token && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                    This reset link is invalid or missing. Request a new reset email to continue.
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                    {error}
                  </Alert>
                )}

                {success ? (
                  <Box sx={{ textAlign: "center" }}>
                    <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: 50, mb: 1.2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.7 }}>
                      Password updated
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1.6 }}>
                      Your password has been changed. You can now sign in with your new password.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate(LOGIN_ROUTE_PATH)}
                      sx={{
                        py: 1.15,
                        px: 2.4,
                        borderRadius: theme.radii?.medium ?? 10,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      }}
                    >
                      Go to sign in {redirectCountdown > 0 ? `(${redirectCountdown}s)` : ""}
                    </Button>
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      id="new-password"
                      label="New password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={!token || isSubmitting}
                      sx={{ mb: 2, ...inputSx }}
                      helperText="Use at least 8 characters, with uppercase, lowercase, number, and symbol."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword((v) => !v)} onMouseDown={(e) => e.preventDefault()} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={password.length > 0 && !isPasswordCompliant(password)}
                    />

                    <PasswordStrengthMeter password={password} />

                    <TextField
                      fullWidth
                      id="confirm-password"
                      label="Confirm new password"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={!token || isSubmitting}
                      sx={{ mb: 2.4, ...inputSx }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirm((v) => !v)} onMouseDown={(e) => e.preventDefault()} edge="end">
                              {showConfirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={confirmPassword.length > 0 && confirmPassword !== password}
                      helperText={confirmPassword && confirmPassword !== password ? "Passwords do not match." : ""}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={!canSubmit}
                      sx={{
                        py: 1.35,
                        borderRadius: theme.radii?.medium ?? 10,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                        },
                      }}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <LockReset />}
                      aria-busy={isSubmitting ? "true" : undefined}
                    >
                      {isSubmitting ? "Updating..." : "Change password"}
                    </Button>

                    {!token && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Need a new link?{" "}
                        <MuiLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(FORGOT_PASSWORD_ROUTE_PATH);
                          }}
                        >
                          Request a new reset email
                        </MuiLink>
                        .
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ textAlign: "center" }}>
                      <Button variant="text" onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                        Back to sign in
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
